export type Language = 'en' | 'hi';

export interface LandingTranslations {
  nav: {
    features: string;
    forSchools: string;
    howItWorks: string;
    faq: string;
    contact: string;
    signIn: string;
    startFree: string;
    signingIn: string;
  };
  hero: {
    pill: string;
    headlinePart1: string;
    headlinePart2: string;
    headlinePart3: string;
    description: string;
    ctaPrimary: string;
    ctaSecondary: string;
    badgeFree: string;
    badgeNoDownload: string;
    badgeAnyDevice: string;
  };
  showcase: {
    tabAttendance: string;
    tabHomework: string;
    tabBroadcast: string;
    rollCallFinished: string;
    gradeRoom: string;
    todaysRoll: string;
    studentName: string;
    oneTapStatus: string;
    present: string;
    absentAlertSent: string;
    mathGrade: string;
    dueTomorrow: string;
    homeworkTitle: string;
    homeworkDesc: string;
    attachedByTeacher: string;
    viewable: string;
    aiEncouragementLabel: string;
    aiEncouragementText: string;
    parentsViewed: string;
    translatedPill: string;
    principalBroadcast: string;
    priorityHigh: string;
    rainAdvisoryTitle: string;
    rainAdvisoryDesc: string;
    deliveredFamilies: string;
    sentStatus: string;
    noSpamBadge: string;
    principalProtected: string;
    clickTabsHint: string;
    tryFreeMini: string;
  };
  roles: {
    sectionTag: string;
    title: string;
    subtitle: string;
    forPrincipal: string;
    principalHeading: string;
    principalDesc: string;
    principalBullet1: string;
    principalBullet2: string;
    principalBullet3: string;
    principalCta: string;
    teacherFavorite: string;
    forTeachers: string;
    teacherHeading: string;
    teacherDesc: string;
    teacherBullet1: string;
    teacherBullet2: string;
    teacherBullet3: string;
    teacherCta: string;
    forParents: string;
    parentHeading: string;
    parentDesc: string;
    parentBullet1: string;
    parentBullet2: string;
    parentBullet3: string;
    parentCta: string;
  };
  features: {
    tag: string;
    title: string;
    subtitle: string;
    f1Title: string;
    f1Desc: string;
    f2Title: string;
    f2Desc: string;
    f3Title: string;
    f3Desc: string;
    f4Title: string;
    f4Desc: string;
    f5Title: string;
    f5Desc: string;
    f6Title: string;
    f6Desc: string;
  };
  comparison: {
    tag: string;
    title: string;
    subtitle: string;
    oldWayTitle: string;
    oldWaySub: string;
    oldWayBadge: string;
    oldWayPoint1: string;
    oldWayPoint2: string;
    oldWayPoint3: string;
    oldWayPoint4: string;
    oldWayResult: string;
    newWayTitle: string;
    newWaySub: string;
    newWayBadge: string;
    newWayRecPill: string;
    newWayPoint1: string;
    newWayPoint2: string;
    newWayPoint3: string;
    newWayPoint4: string;
    newWayResult: string;
  };
  coachingSpotlight: {
    tag: string;
    title: string;
    subtitle: string;
    badge: string;
    schoolsCardTitle: string;
    schoolsCardBadge: string;
    schoolsCardDesc: string;
    schoolItem1Title: string;
    schoolItem1Desc: string;
    schoolItem2Title: string;
    schoolItem2Desc: string;
    schoolItem3Title: string;
    schoolItem3Desc: string;
    schoolItem4Title: string;
    schoolItem4Desc: string;
    coachingCardTitle: string;
    coachingCardBadge: string;
    coachingCardDesc: string;
    coachingItem1Title: string;
    coachingItem1Desc: string;
    coachingItem2Title: string;
    coachingItem2Desc: string;
    coachingItem3Title: string;
    coachingItem3Desc: string;
    coachingItem4Title: string;
    coachingItem4Desc: string;
    bottomBanner: string;
    ctaButton: string;
  };
  testimonials: {
    tag: string;
    title: string;
    subtitle: string;
    t1Quote: string;
    t1Author: string;
    t1Role: string;
    t2Quote: string;
    t2Author: string;
    t2Role: string;
    t3Quote: string;
    t3Author: string;
    t3Role: string;
    t4Quote: string;
    t4Author: string;
    t4Role: string;
  };
  privacy: {
    tag: string;
    title: string;
    desc: string;
    b1Title: string;
    b1Sub: string;
    b2Title: string;
    b2Sub: string;
    b3Title: string;
    b3Sub: string;
    b4Title: string;
    b4Sub: string;
  };
  howItWorks: {
    tag: string;
    title: string;
    subtitle: string;
    step1Title: string;
    step1Desc: string;
    step2Title: string;
    step2Desc: string;
    step3Title: string;
    step3Desc: string;
    ctaBottom: string;
  };
  faq: {
    tag: string;
    title: string;
    q1: string;
    a1: string;
    q2: string;
    a2: string;
    q3: string;
    a3: string;
    q4: string;
    a4: string;
    q5: string;
    a5: string;
    q6: string;
    a6: string;
    q7: string;
    a7: string;
  };
  contact: {
    tag: string;
    title: string;
    desc: string;
    btnCta: string;
    cloudBadge: string;
  };
  footer: {
    tagline: string;
    copyright: string;
  };
}

export const translations: Record<Language, LandingTranslations> = {
  en: {
    nav: {
      features: "Features",
      forSchools: "Schools",
      howItWorks: "How It Works",
      faq: "FAQ",
      contact: "Contact",
      signIn: "Sign In",
      startFree: "Start Free",
      signingIn: "Signing In..."
    },
    hero: {
      pill: "For Schools • Coaching Institutes • Tuition Centers",
      headlinePart1: "Happy Teachers & Faculty.",
      headlinePart2: "Informed Parents & Aspirants.",
      headlinePart3: "Thriving Classrooms & Batches.",
      description: "Say goodbye to endless WhatsApp group chaos, missed DPPs (Daily Practice Problems), and late-night calls. EduGravity unites morning roll calls, batch attendance, homework with photos, and instant alerts into one simple, calm workspace.",
      ctaPrimary: "Start Using for Free",
      ctaSecondary: "See How It Works",
      badgeFree: "100% Free to Use",
      badgeNoDownload: "No app download needed",
      badgeAnyDevice: "Works on any phone, tablet, or PC"
    },
    showcase: {
      tabAttendance: "1. Roll Call & Batches",
      tabHomework: "2. Homework & DPPs",
      tabBroadcast: "3. Campus & Batch Alerts",
      rollCallFinished: "Roll Call Finished",
      gradeRoom: "Grade 4 • Evening JEE Prep Batch",
      todaysRoll: "Today's Attendance Roll",
      studentName: "Student Name",
      oneTapStatus: "1-Tap Status",
      present: "Present ✓",
      absentAlertSent: "Absent (Parent Alert Sent)",
      mathGrade: "Mathematics • Advanced Class 10",
      dueTomorrow: "Due Tomorrow 9:00 AM",
      homeworkTitle: "Fractions & DPP 4: Chapter Exercises",
      homeworkDesc: "Complete questions 1 through 8 on Page 42. Bring your notebook or DPP sheet for peer review!",
      attachedByTeacher: "Attached by Teacher / Faculty Mrs. Anita",
      viewable: "Viewable",
      aiEncouragementLabel: "AI Encouragement:",
      aiEncouragementText: "Great effort practicing questions today! Take your time on #6.",
      parentsViewed: "✓ 24 Parents & Students Viewed",
      translatedPill: "1-Tap Translated: Hindi, Spanish, Tamil",
      principalBroadcast: "Leadership Announcement",
      priorityHigh: "Priority High",
      rainAdvisoryTitle: "Heavy Rain Advisory — Early Dismissal & Online Doubt Session",
      rainAdvisoryDesc: "Dear Parents & Students, due to waterlogging, daytime classes conclude at 2:00 PM and evening coaching doubt clinic is moved to live portal. School buses will depart promptly.",
      deliveredFamilies: "Delivered to 420 Families & Aspirants",
      sentStatus: "100% Sent",
      noSpamBadge: "Zero WhatsApp Spam • Direct Notification",
      principalProtected: "Leader Verified",
      clickTabsHint: "Click tabs above to test the live preview",
      tryFreeMini: "Try Free →"
    },
    roles: {
      sectionTag: "Made for Real Educators & Families",
      title: "Designed for schools, academies & coaching institutes.",
      subtitle: "No technical expertise needed. Whether you lead a K-12 school, manage high-stakes test-prep batches, or guide your child at home, EduGravity is your simplest helper.",
      forPrincipal: "For Principals & Institute Directors",
      principalHeading: "Lead with Calm & Complete Clarity",
      principalDesc: "Oversee your entire school or coaching center from your phone or desk without drowning in phone calls, paper memos, or fragmented WhatsApp groups.",
      principalBullet1: "Invite Vice Principals or Branch Managers with 1-click administrative delegation",
      principalBullet2: "Institution-wide alerts that reach every parent and aspirant's phone in seconds",
      principalBullet3: "Instant attendance rings to see live counts across every classroom and coaching batch",
      principalCta: "Start as Institution Leader",
      teacherFavorite: "Teacher & Faculty Favorite",
      forTeachers: "For Teachers & Coaching Faculty",
      teacherHeading: "More Teaching, Zero Clerical Drag",
      teacherDesc: "Reclaim your personal evenings. Take morning or batch roll call in 10 seconds, share homework and DPPs with photos, and protect your private phone number.",
      teacherBullet1: "One-tap attendance saves 15 minutes of precious class & lecture time",
      teacherBullet2: "AI homework feedback offers encouraging rubric suggestions & student cheer",
      teacherBullet3: "100% private phone numbers: message families without giving out personal WhatsApp",
      teacherCta: "Join as Educator / Faculty",
      forParents: "For Parents & Students",
      parentHeading: "Connected Every Day, Stress-Free",
      parentDesc: "Never wonder 'did my child reach school or tuition?' or 'what homework/DPP is due tonight?'. Get immediate clarity right on your phone without clutter.",
      parentBullet1: "Instant absence and arrival alerts for absolute peace of mind",
      parentBullet2: "Live Homework Board with clear photos of textbook pages, DPP sheets & deadlines",
      parentBullet3: "Send 1-tap sick notes to teachers before class starts",
      parentCta: "Connect as Parent / Student"
    },
    features: {
      tag: "Clear & Visual",
      title: "Everything schools & coaching institutes need, made delightfully simple.",
      subtitle: "Color-coded buttons, clear progress rings, and readable font sizes make running your school or academy easy for everyone.",
      f1Title: "Color-Coded Attendance Rings",
      f1Desc: "Green for present, red for absent. Principals and Institute Directors see an instant visual ring of the day's attendance without sorting through paperwork.",
      f2Title: "AI Homework & DPP Feedback",
      f2Desc: "Helpful suggestions written in encouraging, warm language to cheer on students and guide their practice problem solving.",
      f3Title: "Co-Admin & Vice Principal Delegation",
      f3Desc: "Assign your trusted co-administrator or branch coordinator in one click. No complicated server setups or technical permissions.",
      f4Title: "Campus & Batch Announcements",
      f4Desc: "Broadcast rainy day alerts, mock exam schedules, or festival holidays directly to staff, students, and parents in seconds.",
      f5Title: "Multi-Lingual Translation",
      f5Desc: "Translate homework instructions and notices into regional family languages with one tap so grandparents and parents can guide the child easily.",
      f6Title: "Private & Secure by Design",
      f6Desc: "Bank-grade Google Cloud security. Teachers and coaching faculties never have to give their private cell phone numbers to parents."
    },
    comparison: {
      tag: "The Clear Difference",
      title: "Say goodbye to communication chaos.",
      subtitle: "See why schools, tuition centers, and families love switching to a calm, organized workspace.",
      oldWayTitle: "The Old Way",
      oldWaySub: "WhatsApp groups, paper slips & registers",
      oldWayBadge: "Exhausting",
      oldWayPoint1: "Endless WhatsApp alerts at 10 PM on teachers' and coaching faculty's private phone numbers.",
      oldWayPoint2: "Lost diary notes, crumpled DPP sheets, and missing homework at the bottom of schoolbags.",
      oldWayPoint3: "Manual paper registers that take 15 minutes every morning or eat up tuition lecture time.",
      oldWayPoint4: "Non-English speaking families left out because notices cannot be translated easily.",
      oldWayResult: "Result: Stressed educators, anxious parents, and disorganized administrators.",
      newWayTitle: "The EduGravity Way",
      newWaySub: "Calm, organized school & institute portal",
      newWayBadge: "Peace of Mind",
      newWayRecPill: "Loved by Teachers & Directors",
      newWayPoint1: "100% private phone numbers. Teachers and coaching staff enjoy peaceful personal evenings.",
      newWayPoint2: "Live Homework & DPP Board on parents' phones with real photos of pages and due dates.",
      newWayPoint3: "10-second roll calls with instant green attendance progress rings for leaders.",
      newWayPoint4: "1-Tap multilingual translation so grandparents and ESL families stay fully connected.",
      newWayResult: "Everything in one free Google sign-in"
    },
    coachingSpotlight: {
      tag: "Built for Every Learning Space",
      title: "Tailored for K-12 Schools and Coaching Academies Alike",
      subtitle: "Whether you run a 1,000-student school, an evening tuition batch, or a multi-branch test-prep institute, EduGravity adapts naturally to your workflow.",
      badge: "Unified Workspace",
      schoolsCardTitle: "K-12 Schools & Preschools",
      schoolsCardBadge: "Classes & Homerooms",
      schoolsCardDesc: "Designed for day schools where structured homerooms, morning roll calls, school diaries, and parent-teacher communication are vital.",
      schoolItem1Title: "Morning Homeroom Attendance",
      schoolItem1Desc: "10-second roll call with instant parent alert if a child is absent.",
      schoolItem2Title: "Digital School Diary",
      schoolItem2Desc: "Replace paper slips with nightly homework board and textbook photos.",
      schoolItem3Title: "Principal Campus Broadcasts",
      schoolItem3Desc: "Send emergency weather closures and holiday circulars in 1 click.",
      schoolItem4Title: "Vice Principal Delegation",
      schoolItem4Desc: "Easily grant administrative co-pilot access to vice principals & staff.",
      coachingCardTitle: "Coaching Institutes & Tuition Centers",
      coachingCardBadge: "Batches & Test-Prep",
      coachingCardDesc: "Engineered for JEE, NEET, Foundation, Commerce, and evening tuition classes with high parent accountability and rapid schedules.",
      coachingItem1Title: "Batch-Wise Attendance & Arrival Alerts",
      coachingItem1Desc: "Reassure parents the moment their child arrives safely at the evening tuition center.",
      coachingItem2Title: "Daily Practice Problems (DPP) with Photos",
      coachingItem2Desc: "Upload problem sheets, assignment questions, and answer keys instantly.",
      coachingItem3Title: "Faculty Phone Number Privacy",
      coachingItem3Desc: "Stop 10 PM doubt calls on teachers' private phones; keep communication inside the app.",
      coachingItem4Title: "Mock Test & Batch Schedule Alerts",
      coachingItem4Desc: "Broadcast Sunday mock test schedules, rank sheets, and revised batch timings with zero spam.",
      bottomBanner: "Works on any device without software downloads or complex installation.",
      ctaButton: "Get Started Free with Google"
    },
    testimonials: {
      tag: "Community Voices",
      title: "Loved by Principals, Directors, Teachers & Families",
      subtitle: "Hear how EduGravity transformed daily communication across schools and coaching academies.",
      t1Quote: "I used to spend an hour every evening answering repetitive WhatsApp messages. EduGravity gave me my personal evenings back. Roll call takes 10 seconds, and parents see homework photos automatically.",
      t1Author: "Mrs. Anita Raman",
      t1Role: "Grade 4 Classroom Teacher",
      t2Quote: "Inviting my Vice Principal took literally one click. During unexpected heavy rainfall last month, our campus broadcast alert reached 500 parents in 20 seconds. No server headache.",
      t2Author: "Dr. Daniel K. Thorne",
      t2Role: "School Principal & Trustee",
      t3Quote: "As a working mom, getting a notification the moment roll call finishes gives me pure peace of mind. And I never have to interrogate my 9-year-old about whether she has homework tonight.",
      t3Author: "Sunita Mehta",
      t3Role: "Parent of 2 Students",
      t4Quote: "Managing 6 evening batches for JEE/NEET prep was absolute WhatsApp madness before EduGravity. Now every batch has its own attendance ring, daily DPP upload, and test alert. Parents love getting the safe arrival alert when their child reaches our institute.",
      t4Author: "Prof. Rajesh Verma",
      t4Role: "Director, Verma Science Academy"
    },
    privacy: {
      tag: "Privacy & Safety Guaranteed",
      title: "Your Learning Community is 100% Protected",
      desc: "We treat student records with the highest institutional care. No advertising, no data reselling, and no personal phone number exposure.",
      b1Title: "Zero Phone Sharing",
      b1Sub: "Faculty stay private",
      b2Title: "Google Cloud",
      b2Sub: "Encrypted at rest",
      b3Title: "Student ID Only",
      b3Sub: "Private family pairing",
      b4Title: "100% Ad-Free",
      b4Sub: "Zero data selling"
    },
    howItWorks: {
      tag: "Super Simple Setup",
      title: "Get started in 3 friendly steps.",
      subtitle: "No IT department needed. You can set up your school or coaching institute during your coffee break.",
      step1Title: "Sign In with Google",
      step1Desc: "Click 'Start for Free' using your existing Google account. No new passwords to remember.",
      step2Title: "Create Your School or Coaching Center",
      step2Desc: "Type your institution name, grades or coaching batches. Add co-administrators if needed.",
      step3Title: "Share With Teachers & Faculty",
      step3Desc: "Send your simple institution code to your staff. Teachers take roll call, upload DPPs, and connect with parents!",
      ctaBottom: "Launch Your Institution Now — It's Free"
    },
    faq: {
      tag: "Got Questions?",
      title: "Common Questions from Schools, Coaching Centers & Families",
      q1: "How easy is EduGravity to use for teachers and parents who aren't tech-savvy?",
      a1: "EduGravity is deliberately designed with zero tech jargon. Large friendly buttons, clear picture previews, and simple color-coded statuses (Present, Absent, Completed) mean any teacher, coaching tutor, or parent can use it within 30 seconds without reading a manual.",
      q2: "How do I get started for my school or coaching institute?",
      a2: "Just click 'Start Using for Free' and sign in with your Google account. You can register your school or coaching institute name in under 2 minutes, set your grades or batches, and invite faculty and co-administrators right away.",
      q3: "Can the Principal or Director assign Vice Principals or Branch Managers to help?",
      a3: "Yes! EduGravity features instant co-admin delegation. Simply add your Vice Principal, Academic Coordinator, or Branch Manager's Gmail. When they sign in, they automatically get administrative access to manage teacher approvals, student lists, and broadcasts.",
      q4: "Do parents, students, and teachers need to download an app from Google Play or App Store?",
      a4: "No downloads required! EduGravity works directly in any phone browser (Safari, Chrome) and prompts you to 'Add to Home Screen' with a single tap. It behaves just like a regular mobile app, taking up zero phone storage.",
      q5: "How does the AI grading helper assist teachers and faculty?",
      a5: "Instead of grading paper by paper until late at night, teachers can get helpful rubric suggestions, positive encouragement tips for each child, and even instant translation for parents who speak other languages.",
      q6: "Is student and family data private and secure?",
      a6: "100% private. Student records, attendance, and grades are protected by Google Cloud security. Only verified teachers and parents with their child's specific Student ID can view schoolwork and attendance.",
      q7: "Can coaching institutes, evening tuition classes, and test-prep academies use EduGravity?",
      a7: "Yes, absolutely! EduGravity is custom-engineered for both schools and coaching institutes. Whether you run a private tuition batch of 20 students, an evening test-prep academy (JEE, NEET, Foundation, Commerce), or multi-branch coaching classes, you can create batches, record attendance with arrival alerts, upload DPPs and practice sheets with photos, post test schedules, and communicate with parents with zero WhatsApp spam and 100% private faculty phone numbers."
    },
    contact: {
      tag: "Friendly Human Support",
      title: "Have questions or want a personal walkthrough?",
      desc: "We're always happy to speak with school principals, trustees, or parent associations. We can guide you through setup step-by-step.",
      btnCta: "Try EduGravity Free",
      cloudBadge: "Google Cloud Infrastructure"
    },
    footer: {
      tagline: "— The Modern School Operating System",
      copyright: "Designed with love for principals, teachers, parents, and students."
    }
  },
  hi: {
    nav: {
      features: "विशेषताएं",
      forSchools: "स्कूल",
      howItWorks: "कार्यप्रणाली",
      faq: "सामान्य प्रश्न",
      contact: "संपर्क",
      signIn: "लॉग इन",
      startFree: "शुरू करें",
      signingIn: "लॉग इन हो रहा है..."
    },
    hero: {
      pill: "स्कूलों, कोचिंग संस्थानों और ट्यूशन सेंटर्स के लिए",
      headlinePart1: "संतुष्ट शिक्षक एवं फैकल्टी।",
      headlinePart2: "जागरूक अभिभावक एवं विद्यार्थी।",
      headlinePart3: "उज्ज्वल कक्षाएं एवं कोचिंग बैच।",
      description: "अनावश्यक व्हाट्सएप संदेशों, खोए हुए डीपीपी (Daily Practice Problems) और देर रात तक फोन कॉल के झंझट को अलविदा कहें। EduGravity सुबह की उपस्थिति, बैच हाजिरी, फोटो सहित होमवर्क और सीधी सूचनाओं को एक व्यवस्थित और शांत स्थान पर लाता है।",
      ctaPrimary: "मुफ़्त में उपयोग शुरू करें",
      ctaSecondary: "देखें यह कैसे काम करता है",
      badgeFree: "उपयोग के लिए 100% मुफ़्त",
      badgeNoDownload: "ऐप डाउनलोड की आवश्यकता नहीं",
      badgeAnyDevice: "किसी भी फ़ोन, टैबलेट या कंप्यूटर पर तुरंत उपलब्ध"
    },
    showcase: {
      tabAttendance: "१. उपस्थिति रिंग एवं बैच",
      tabHomework: "२. होमवर्क एवं डीपीपी बोर्ड",
      tabBroadcast: "३. संस्थान एवं बैच अलर्ट",
      rollCallFinished: "उपस्थिति दर्ज संपन्न",
      gradeRoom: "कक्षा ४ • शाम का जेईई फाउंडेशन बैच",
      todaysRoll: "आज की बैच उपस्थिति",
      studentName: "विद्यार्थी का नाम",
      oneTapStatus: "१-टैप स्टेटस",
      present: "उपस्थित ✓",
      absentAlertSent: "अनुपस्थित (अभिभावक को सूचना भेजी गई)",
      mathGrade: "गणित • एडवांस्ड क्लास १०",
      dueTomorrow: "जमा करने का समय: कल सुबह ९:०० बजे",
      homeworkTitle: "भिन्न और डीपीपी ४: अध्याय के अभ्यास प्रश्न",
      homeworkDesc: "पृष्ठ ४२ पर प्रश्न १ से ८ तक हल करें। कक्षा या कोचिंग में चर्चा के लिए अपनी डीपीपी शीट साथ लाएं!",
      attachedByTeacher: "शिक्षिका/फैकल्टी श्रीमती अनीता द्वारा संलग्न",
      viewable: "देखें",
      aiEncouragementLabel: "एआई प्रोत्साहन संदेश:",
      aiEncouragementText: "आज अभ्यास प्रश्न हल करने का बहुत अच्छा प्रयास! प्रश्न ६ पर थोड़ा ध्यान दें।",
      parentsViewed: "✓ २४ अभिभावकों एवं विद्यार्थियों ने देखा",
      translatedPill: "१-टैप अनुवाद: हिंदी, अंग्रेज़ी, तमिल",
      principalBroadcast: "संस्थान प्रमुख का संदेश (ब्रॉडकास्ट)",
      priorityHigh: "अति महत्वपूर्ण",
      rainAdvisoryTitle: "भारी बारिश की चेतावनी — स्कूल समय पूर्व विदा व शाम की ऑनलाइन डाउट क्लास",
      rainAdvisoryDesc: "प्रिय अभिभावक एवं विद्यार्थी, भारी बारिश और जलभराव के कारण आज स्कूल दोपहर २:०० बजे समाप्त होगा और शाम की कोचिंग डाउट क्लास ऑनलाइन पोर्टल पर होगी।",
      deliveredFamilies: "४२० परिवारों एवं छात्रों को भेजा गया",
      sentStatus: "१००% संदेश पहुंचे",
      noSpamBadge: "व्हाट्सएप स्पैम से मुक्ति • सीधी सूचना",
      principalProtected: "प्रमाणित एवं सुरक्षित",
      clickTabsHint: "लाइव डेमो देखने के लिए ऊपर टैब पर क्लिक करें",
      tryFreeMini: "मुफ़्त आज़माएं →"
    },
    roles: {
      sectionTag: "आपकी आवश्यकताओं के अनुरूप",
      title: "स्कूलों, अकादमियों और कोचिंग संस्थानों के लिए निर्मित।",
      subtitle: "किसी तकनीकी ज्ञान की आवश्यकता नहीं। चाहे आप स्कूल के प्रिंसिपल हों, कोचिंग निदेशक हों, फैकल्टी हों या बच्चे के माता-पिता—EduGravity आपका सबसे सरल साथी है।",
      forPrincipal: "प्रिंसिपलों एवं कोचिंग निदेशकों के लिए",
      principalHeading: "शांति और पूर्ण स्पष्टता से संस्थान का संचालन करें",
      principalDesc: "लगातार फोन कॉल, कागजी रजिस्टरों या अनगिनत व्हाट्सएप ग्रुपों के बिना अपने फोन से पूरे स्कूल या कोचिंग सेंटर की गतिविधि पर नज़र रखें।",
      principalBullet1: "वाइस-प्रिंसिपल या ब्रांच हेड को केवल १ क्लिक में प्रशासनिक अधिकार सौंपें",
      principalBullet2: "संस्थान-स्तरीय घोषणाएं जो कुछ ही सेकंड में हर अभिभावक और छात्र के फोन तक पहुँचती हैं",
      principalBullet3: "लाइव उपस्थिति रिंग—तुरंत जानें हर कक्षा और कोचिंग बैच में कितने विद्यार्थी मौजूद हैं",
      principalCta: "संस्थान प्रमुख के रूप में शुरू करें",
      teacherFavorite: "शिक्षकों एवं कोचिंग फैकल्टी की पहली पसंद",
      forTeachers: "शिक्षकों एवं कोचिंग फैकल्टी के लिए",
      teacherHeading: "अधिक अध्यापन, शून्य कागजी झंझट",
      teacherDesc: "अपनी शामें बचाएं। सुबह या शाम के बैच की हाजिरी मात्र १० सेकंड में लें, फोटो के साथ होमवर्क व डीपीपी साझा करें और अपना निजी फोन नंबर सुरक्षित रखें।",
      teacherBullet1: "१-टैप हाजिरी, जिससे हर कक्षा और लेक्चर के १५ मिनट बचते हैं",
      teacherBullet2: "एआई होमवर्क सहायक विद्यार्थियों के लिए उत्साहवर्धक सुझाव व मार्गदर्शन देता है",
      teacherBullet3: "१००% निजी फोन नंबर: व्हाट्सएप नंबर साझा किए बिना अभिभावकों से सीधा संपर्क करें",
      teacherCta: "शिक्षक/फैकल्टी के रूप में जुड़ें",
      forParents: "अभिभावकों एवं विद्यार्थियों के लिए",
      parentHeading: "तनाव-मुक्त होकर हर दिन जुड़े रहें",
      parentDesc: "कभी यह चिंता न करें कि 'क्या मेरा बच्चा स्कूल या कोचिंग पहुंचा?' या 'आज कौन सा होमवर्क/डीपीपी मिला है?'. सारी स्पष्ट जानकारी सीधे अपने फोन पर पाएं।",
      parentBullet1: "अनुपस्थिति और आगमन की तुरंत सूचना ताकि आपको रहे पूरी तसल्ली",
      parentBullet2: "किताब के पन्नों व डीपीपी की फोटो और अंतिम तिथि के साथ लाइव होमवर्क बोर्ड",
      parentBullet3: "यदि बच्चा अस्वस्थ है तो कक्षा से पूर्व १ टैप में शिक्षक को सूचित करें",
      parentCta: "अभिभावक/विद्यार्थी के रूप में जुड़ें"
    },
    features: {
      tag: "स्पष्ट और दृश्यमान",
      title: "स्कूल और कोचिंग संस्थान की हर ज़रूरत, बेहद सरल तरीके से।",
      subtitle: "रंग-कोडित बटन, स्पष्ट प्रगति चक्र और बड़े अक्षर संस्थान के प्रबंधन को हर किसी के लिए आसान बनाते हैं।",
      f1Title: "कलर-कोडित उपस्थिति चक्र",
      f1Desc: "उपस्थित के लिए हरा, अनुपस्थित के लिए लाल। प्रिंसिपल और कोचिंग डायरेक्टर बिना कागज़ पलटे एक नज़र में पूरी उपस्थिति देख सकते हैं।",
      f2Title: "एआई होमवर्क एवं डीपीपी सुझाव",
      f2Desc: "विद्यार्थियों का उत्साह बढ़ाने और अभ्यास समस्याओं को हल करने में सही मार्गदर्शन देने के लिए प्रेरक सुझाव।",
      f3Title: "सह-प्रशासक एवं वाइस-प्रिंसिपल प्रबंधन",
      f3Desc: "एक क्लिक में अपने वाइस-प्रिंसिपल या कोचिंग ब्रांच कोऑर्डिनेटर को प्रशासनिक ज़िम्मेदारी सौंपें।",
      f4Title: "कैंपस एवं बैच की त्वरित घोषणाएं",
      f4Desc: "बारिश की छुट्टी, मॉक टेस्ट कार्यक्रम या छुट्टियों की सूचनाएं कुछ ही सेकंड में पूरे स्टाफ, छात्रों और परिवारों तक पहुंचाएं।",
      f5Title: "बहुभाषी अनुवाद",
      f5Desc: "होमवर्क के निर्देशों को अपनी स्थानीय भाषा में १ टैप में अनुवाद करें ताकि घर के सभी सदस्य बच्चे की मदद कर सकें।",
      f6Title: "गोपनीय और सुरक्षित",
      f6Desc: "गूगल क्लाउड की उच्चतम सुरक्षा। शिक्षकों और कोचिंग फैकल्टी को कभी भी अपने निजी मोबाइल नंबर साझा नहीं करने पड़ते।"
    },
    comparison: {
      tag: "स्पष्ट अंतर",
      title: "संवाद की अव्यवस्था को कहें अलविदा।",
      subtitle: "जानें क्यों स्कूल, ट्यूशन क्लासेस और अभिभावक EduGravity के शांत और व्यवस्थित वातावरण को पसंद करते हैं।",
      oldWayTitle: "पुराना तरीका",
      oldWaySub: "व्हाट्सएप ग्रुप, कागजी पर्चियां और भारी रजिस्टर",
      oldWayBadge: "तनावपूर्ण",
      oldWayPoint1: "रात १० बजे शिक्षकों और फैकल्टी के व्यक्तिगत फोन पर लगातार व्हाट्सएप संदेश।",
      oldWayPoint2: "स्कूल बैग के नीचे खोई हुई डायरी पर्चियां, मुड़ी हुई डीपीपी और गायब होमवर्क।",
      oldWayPoint3: "हाजिरी रजिस्टर में हर सुबह १५ मिनट या ट्यूशन लेक्चर का कीमती समय खराब होना।",
      oldWayPoint4: "क्षेत्रीय भाषाएं बोलने वाले परिवारों को सही समय पर सूचना न मिल पाना।",
      oldWayResult: "परिणाम: परेशान शिक्षक, चिंतित माता-पिता और अव्यवस्थित संस्थान संचालक।",
      newWayTitle: "EduGravity का तरीका",
      newWaySub: "शांत, सुव्यवस्थित स्कूल एवं कोचिंग पोर्टल",
      newWayBadge: "सुकून और शांति",
      newWayRecPill: "शिक्षकों और निदेशकों द्वारा अनुशंसित",
      newWayPoint1: "१००% निजी फोन नंबर। शिक्षक और फैकल्टी अपने परिवार के साथ शांतिपूर्ण शामें बिताते हैं।",
      newWayPoint2: "अभिभावकों के फोन पर फोटो सहित लाइव होमवर्क और डीपीपी बोर्ड।",
      newWayPoint3: "१० सेकंड की उपस्थिति और संस्थान प्रमुखों के लिए हरा प्रगति चक्र।",
      newWayPoint4: "१-टैप बहुभाषी अनुवाद ताकि हर परिवार पूरी तरह जुड़ा रहे।",
      newWayResult: "एक मुफ़्त गूगल लॉगिन में सब कुछ उपलब्ध"
    },
    coachingSpotlight: {
      tag: "हर शिक्षण संस्थान के लिए उपयुक्त",
      title: "स्कूलों और कोचिंग अकादमियों दोनों के लिए विशेष रूप से निर्मित",
      subtitle: "चाहे आप १,००० विद्यार्थियों का स्कूल चलाते हों, शाम का ट्यूशन बैच, या कई शाखाओं वाली टेस्ट-प्रेप एकेडमी—EduGravity आपकी हर ज़रूरत पूरी करता है।",
      badge: "एकीकृत कार्यक्षेत्र",
      schoolsCardTitle: "के-१२ स्कूल एवं प्ले-स्कूल",
      schoolsCardBadge: "कक्षाएं और होमरूम",
      schoolsCardDesc: "डे-स्कूलों के लिए आदर्श जहाँ सुबह की उपस्थिति, स्कूल डायरी, कक्षा गतिविधियाँ और अभिभावक-शिक्षक संवाद मुख्य होते हैं।",
      schoolItem1Title: "सुबह की कक्षा-वार उपस्थिति",
      schoolItem1Desc: "१० सेकंड की हाजिरी और अनुपस्थित होने पर अभिभावक को तुरंत स्वचालित सूचना।",
      schoolItem2Title: "डिजिटल स्कूल डायरी",
      schoolItem2Desc: "कागजी पर्चियों की जगह फोटो सहित दैनिक होमवर्क बोर्ड और तारीखें।",
      schoolItem3Title: "प्रिंसिपल कैंपस घोषणाएं",
      schoolItem3Desc: "मौसम की चेतावनी, छुट्टी और परीक्षा सर्कुलर १ क्लिक में सभी तक भेजें।",
      schoolItem4Title: "वाइस-प्रिंसिपल सह-संचालन",
      schoolItem4Desc: "वाइस-प्रिंसिपल या ऑफिस हेड को प्रशासनिक अधिकार आसानी से सौंपें।",
      coachingCardTitle: "कोaching संस्थान एवं ट्यूशन क्लासेस",
      coachingCardBadge: "बैच एवं टेस्ट-प्रेप",
      coachingCardDesc: "जेईई, नीट, कॉमर्स, फाउंडेशन और शाम के ट्यूशन बैचों के लिए सटीक व्यवस्था जहाँ सुरक्षित आगमन और नियमित डीपीपी आवश्यक हैं।",
      coachingItem1Title: "बैच-वार उपस्थिति व आगमन अलर्ट",
      coachingItem1Desc: "विद्यार्थी के शाम के कोचिंग सेंटर पहुंचते ही माता-पिता को सुरक्षित आगमन की तसल्ली।",
      coachingItem2Title: "फोटो सहित डेली प्रैक्टिस प्रॉब्लम्स (DPP)",
      coachingItem2Desc: "अभ्यास प्रश्न पत्र, असाइनमेंट और उत्तर कुंजी तुरंत साझा करें।",
      coachingItem3Title: "फैकल्टी के निजी फोन नंबर की सुरक्षा",
      coachingItem3Desc: "शिक्षकों के निजी फोन पर रात १० बजे आने वाले डाउट कॉल्स बंद; केवल ऐप में संवाद।",
      coachingItem4Title: "मॉक टेस्ट व बैच टाइमिंग अलर्ट",
      coachingItem4Desc: "रविवार के मॉक टेस्ट का समय, रैंक लिस्ट और नए बैच की सूचना बिना स्पैम के भेजें।",
      bottomBanner: "बिना किसी ऐप डाउनलोड या जटिल सॉफ्टवेयर इंस्टॉलेशन के किसी भी डिवाइस पर तुरंत चलता है।",
      ctaButton: "गूगल द्वारा मुफ़्त शुरू करें"
    },
    testimonials: {
      tag: "स्कूल एवं कोचिंग समुदाय की आवाज़",
      title: "प्रिंसिपलों, निदेशकों, शिक्षकों और परिवारों द्वारा सराहा गया",
      subtitle: "सुनिए कैसे EduGravity ने स्कूलों और कोचिंग संस्थानों में दैनिक संवाद को बदल दिया।",
      t1Quote: "मैं रोज़ शाम को एक घंटा व्हाट्सएप पर वही सवाल दोहराने में बिताती थी। EduGravity ने मुझे मेरी शामें लौटा दीं। हाजिरी १० सेकंड में हो जाती है और माता-पिता को होमवर्क फोटो अपने आप दिख जाती है।",
      t1Author: "श्रीमती अनीता रमन",
      t1Role: "कक्षा ४ अध्यापिका",
      t2Quote: "अपने वाइस-प्रिंसिपल को एडमिन बनाना सिर्फ १ क्लिक का काम था। पिछले महीने अप्रत्याशित भारी बारिश में हमारा स्कूल अलर्ट २० सेकंड में ५०० अभिभावकों तक पहुंच गया। कोई तकनीकी झंझट नहीं।",
      t2Author: "डॉ. डैनियल के. थोर्न",
      t2Role: "स्कूल प्रिंसिपल एवं ट्रस्टी",
      t3Quote: "एक कामकाजी माँ होने के नाते, हाजिरी पूरी होते ही फोन पर नोटिफिकेशन आना मुझे बहुत तसल्ली देता है। और मुझे अपनी ९ साल की बेटी से बार-बार पूछना नहीं पड़ता कि आज क्या होमवर्क मिला है।",
      t3Author: "सुनीता मेहता",
      t3Role: "२ बच्चों की माता",
      t4Quote: "जेईई और नीट की तैयारी के लिए शाम के ६ बैचों का प्रबंधन पहले व्हाट्सएप पर बहुत अव्यवस्थित था। अब EduGravity से हर बैच की अलग हाजिरी रिंग, डीपीपी और टेस्ट की सूचना तुरंत पहुंच जाती है। अभिभावक कोचिंग पहुंचने का अलर्ट पाकर निश्चिंत रहते हैं।",
      t4Author: "प्रो. राजेश वर्मा",
      t4Role: "निदेशक, वर्मा साइंस एकेडमी (कोटा)"
    },
    privacy: {
      tag: "गोपनीयता और सुरक्षा की गारंटी",
      title: "आपका शिक्षण समुदाय १००% सुरक्षित है",
      desc: "हम विद्यार्थियों के रिकॉर्ड को अत्यंत ज़िम्मेदारी और सुरक्षा के साथ रखते हैं। कोई विज्ञापन नहीं, कोई डेटा बेचना नहीं, और कोई व्यक्तिगत फोन नंबर नहीं दिखता।",
      b1Title: "निजी फोन नंबर सुरक्षित",
      b1Sub: "फैकल्टी की गोपनीयता बनी रहे",
      b2Title: "गूगल क्लाउड सुरक्षा",
      b2Sub: "एन्क्रिप्टेड डेटा",
      b3Title: "केवल स्टूडेंट आईडी",
      b3Sub: "सुरक्षित पारिवारिक जुड़ाव",
      b4Title: "१००% विज्ञापन-मुक्त",
      b4Sub: "डेटा कभी नहीं बेचा जाता"
    },
    howItWorks: {
      tag: "अत्यंत आसान शुरुआत",
      title: "३ सरल चरणों में शुरू करें।",
      subtitle: "किसी आईटी विशेषज्ञ की ज़रूरत नहीं। आप चाय के ब्रेक में अपने स्कूल या कोचिंग सेंटर को सेटअप कर सकते हैं।",
      step1Title: "गूगल से लॉग इन करें",
      step1Desc: "अपने मौजूदा गूगल अकाउंट से 'मुफ़्त शुरू करें' पर क्लिक करें। कोई नया पासवर्ड याद रखने का झंझट नहीं।",
      step2Title: "अपने स्कूल या कोचिंग सेंटर का नाम दर्ज करें",
      step2Desc: "अपने संस्थान का नाम और कक्षाएं/बैच दर्ज करें। यदि चाहें तो सह-प्रशासक का ईमेल भी जोड़ें।",
      step3Title: "शिक्षकों एवं फैकल्टी के साथ साझा करें",
      step3Desc: "अपने स्टाफ को संस्थान का सरल कोड भेजें। शिक्षक जुड़ेंगे, हाजिरी लेंगे, डीपीपी अपलोड करेंगे और माता-पिता से आसानी से जुड़ेंगे!",
      ctaBottom: "अपने संस्थान के लिए अभी शुरू करें — यह मुफ़्त है"
    },
    faq: {
      tag: "आपके प्रश्न",
      title: "स्कूलों, कोचिंग संस्थानों और परिवारों के सामान्य प्रश्न",
      q1: "क्या यह उन शिक्षकों और माता-पिता के लिए आसान है जो तकनीक के बहुत जानकार नहीं हैं?",
      a1: "EduGravity को बिना किसी तकनीकी जटिलता के तैयार किया गया है। बड़े स्पष्ट बटन, फोटो प्रीव्यू और रंग-कोडित स्टेटस (उपस्थित, अनुपस्थित, संपन्न) का मतलब है कि कोई भी शिक्षक, ट्यूशन फैकल्टी या अभिभावक बिना किसी ट्रेनिंग के ३० सेकंड में इसका उपयोग शुरू कर सकता है।",
      q2: "मैं अपने स्कूल या कोचिंग संस्थान के लिए शुरुआत कैसे करूँ?",
      a2: "बस 'मुफ़्त में उपयोग शुरू करें' पर क्लिक करें और अपने गूगल अकाउंट से लॉग इन करें। आप २ मिनट में अपने संस्थान का नाम दर्ज कर सकते हैं, कक्षाएं या बैच चुन सकते हैं और फैकल्टी को आमंत्रित कर सकते हैं।",
      q3: "क्या प्रिंसिपल या निदेशक वाइस-प्रिंसिपल या ब्रांच मैनेजर को मदद के लिए जोड़ सकते हैं?",
      a3: "हाँ! EduGravity में तुरंत सह-प्रशासक (Co-Admin) जोड़ने की सुविधा है। बस अपने वाइस-प्रिंसिपल या ब्रांच कोऑर्डिनेटर की जीमेल आईडी जोड़ें। उनके लॉग इन करते ही उन्हें शिक्षक अनुमोदन, छात्र सूची और घोषणाओं के प्रबंधन के अधिकार मिल जाएंगे।",
      q4: "क्या अभिभावकों, छात्रों या शिक्षकों को कोई ऐप डाउनलोड करनी होगी?",
      a4: "किसी ऐप डाउनलोड की आवश्यकता नहीं है! EduGravity किसी भी फोन ब्राउज़र (क्रोम, सफारी) में तुरंत खुलता है और केवल एक टैप में 'होम स्क्रीन पर जोड़ें' का विकल्प देता है। यह बिना फोन का स्टोरेज भरे बिल्कुल ऐप की तरह काम करता है।",
      q5: "एआई ग्रेडिंग सहायक शिक्षकों और फैकल्टी की कैसे मदद करता है?",
      a5: "देर रात तक कागज़ जांचने के बजाय, शिक्षक प्रत्येक बच्चे के लिए उत्साहवर्धक टिप्पणियां, रूब्रिक सुझाव और अन्य भाषा बोलने वाले माता-पिता के लिए तुरंत अनुवाद प्राप्त कर सकते हैं।",
      q6: "क्या विद्यार्थियों और परिवारों का डेटा निजी और सुरक्षित है?",
      a6: "१००% निजी और सुरक्षित। सभी रिकॉर्ड गूगल क्लाउड सिक्योरिटी द्वारा सुरक्षित हैं। केवल अधिकृत शिक्षक और अपने बच्चे की विशिष्ट स्टूडेंट आईडी वाले माता-पिता ही होमवर्क और उपस्थिति देख सकते हैं।",
      q7: "क्या कोचिंग संस्थान, शाम की ट्यूशन क्लासेस और टेस्ट-प्रेप एकेडमी EduGravity का उपयोग कर सकते हैं?",
      a7: "हाँ, बिल्कुल! EduGravity को स्कूलों और कोचिंग संस्थानों दोनों के लिए विशेष रूप से तैयार किया गया है। चाहे आप २० बच्चों का व्यक्तिगत ट्यूशन बैच चलाते हों, या जेईई, नीट, फाउंडेशन और कॉमर्स की तैयारी कराने वाली कोचिंग एकेडमी, आप बैच बना सकते हैं, बैच-वार उपस्थिति ले सकते हैं, सुरक्षित आगमन के अलर्ट भेज सकते हैं, फोटो के साथ डीपीपी (Daily Practice Problems) साझा कर सकते हैं और फैकल्टी का निजी नंबर छिपाकर माता-पिता से सीधा संवाद कर सकते हैं।"
    },
    contact: {
      tag: "सहज मानवीय सहायता",
      title: "कोई प्रश्न है या व्यक्तिगत डेमो देखना चाहते हैं?",
      desc: "हम स्कूल प्रिंसिपलों, ट्रस्टियों या अभिभावक संघों से बात करने में हमेशा प्रसन्नता महसूस करते हैं। हम चरण-दर-चरण सेटअप में आपकी सहायता करेंगे।",
      btnCta: "EduGravity मुफ़्त आज़माएं",
      cloudBadge: "गूगल क्लाउड इंफ्रास्ट्रक्चर"
    },
    footer: {
      tagline: "— स्कूलों के लिए आधुनिक ऑपरेटिंग सिस्टम",
      copyright: "प्रिंसिपलों, शिक्षकों, माता-पिता और विद्यार्थियों के लिए प्रेमपूर्वक समर्पित।"
    }
  }
};
